/**
 * ============================================================
 * useAnalyticsTracker.ts — Rastreamento de Sessoes e Features
 * ============================================================
 *
 * Responsabilidade: Rastrear sessoes de usuario, paginas visitadas
 *   e uso de funcionalidades, persistindo na tabela analytics_sessions.
 *
 * Retorno: { trackFeature } — funcao para registrar uso de feature.
 *
 * Efeitos colaterais:
 *   - Cria sessao no Supabase ao montar (insert em analytics_sessions)
 *   - Atualiza paginas visitadas e duracao a cada navegacao
 *   - Encerra sessao no beforeunload (calcula duracao total)
 *   - setupGlobalErrorTracking registra erros JS e rejeicoes de Promise
 *
 * Quando usar: No AppLayout para rastrear toda a navegacao.
 *   trackFeature() para instrumentar features especificas.
 * ============================================================
 */

import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useStore } from '@/store/useStore';

/** ID da sessao atual — acessivel globalmente para correlacao de erros */
let currentSessionId: string | null = null;

/**
 * Hook de rastreamento de analytics por sessao.
 * Cria sessao ao montar, rastreia paginas e encerra no unload.
 */
export function useAnalyticsTracker() {
  const location = useLocation();
  const activeProjectId = useStore((s) => s.activeProjectId);
  const sessionRef = useRef<string | null>(null);
  const pagesRef = useRef<string[]>([]);
  const startRef = useRef<number>(Date.now());

  // Start session
  useEffect(() => {
    if (!activeProjectId) return;

    const startSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) return;

      const { data } = await supabase.from('analytics_sessions').insert({
        user_id: user.id,
        project_id: activeProjectId,
        user_agent: navigator.userAgent,
        pages_visited: [location.pathname],
      }).select('id').single();

      if (data) {
        sessionRef.current = data.id;
        currentSessionId = data.id;
        startRef.current = Date.now();
        pagesRef.current = [location.pathname];
      }
    };

    startSession();

    // End session on unload — use supabase client (proper auth headers)
    const endSession = () => {
      if (!sessionRef.current) return;
      const duration = Math.round((Date.now() - startRef.current) / 1000);
      supabase.from('analytics_sessions').update({
        ended_at: new Date().toISOString(),
        duration_seconds: duration,
        pages_visited: pagesRef.current,
      }).eq('id', sessionRef.current).then(() => {}, () => {});
    };

    window.addEventListener('beforeunload', endSession);
    return () => {
      endSession();
      window.removeEventListener('beforeunload', endSession);
    };
  }, [activeProjectId]);

  // Track page visits
  useEffect(() => {
    if (!sessionRef.current || !activeProjectId) return;
    const path = location.pathname;
    if (!pagesRef.current.includes(path)) {
      pagesRef.current.push(path);
    }

    // Update session pages
    supabase.from('analytics_sessions').update({
      pages_visited: pagesRef.current,
      duration_seconds: Math.round((Date.now() - startRef.current) / 1000),
    }).eq('id', sessionRef.current).then(() => {}, () => {});
  }, [location.pathname, activeProjectId]);

  // Track feature usage
  const trackFeature = useCallback(async (featureName: string) => {
    if (!activeProjectId) return;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    await supabase.from('analytics_feature_usage').insert({
      user_id: user.id,
      project_id: activeProjectId,
      feature_name: featureName,
      page_path: location.pathname,
    });
  }, [activeProjectId, location.pathname]);

  return { trackFeature };
}

/**
 * Registra listeners globais para capturar erros JS e rejeicoes de Promise.
 * Persiste em analytics_errors para monitoramento no painel admin.
 *
 * @param projectId - ID do projeto ativo para associar os erros
 * @returns Funcao de cleanup para remover os listeners
 */
export function setupGlobalErrorTracking(projectId: string | null) {
  const handler = async (event: ErrorEvent) => {
    if (!projectId) return;
    const { data: { user } } = await supabase.auth.getUser();
    await supabase.from('analytics_errors').insert({
      user_id: user?.id || null,
      project_id: projectId,
      error_message: event.message || 'Unknown error',
      error_stack: event.error?.stack || '',
      page_path: window.location.pathname,
      user_agent: navigator.userAgent,
    });
  };

  const rejectionHandler = async (event: PromiseRejectionEvent) => {
    if (!projectId) return;
    const { data: { user } } = await supabase.auth.getUser();
    await supabase.from('analytics_errors').insert({
      user_id: user?.id || null,
      project_id: projectId,
      error_message: String(event.reason?.message || event.reason || 'Unhandled rejection'),
      error_stack: event.reason?.stack || '',
      page_path: window.location.pathname,
      user_agent: navigator.userAgent,
    });
  };

  window.addEventListener('error', handler);
  window.addEventListener('unhandledrejection', rejectionHandler);

  return () => {
    window.removeEventListener('error', handler);
    window.removeEventListener('unhandledrejection', rejectionHandler);
  };
}
